/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useState, useEffect } from 'react'
import GameAttendanceCarousel from '../../components/GameAttendanceCarousel'
import DivisionStandingsCarousel from '../../components/DivisionStandingsCarousel'
import { useAttendance } from '@sportninja/common/hooks/useAttendance'
import styled from '@emotion/styled/macro'
import ManageTeamAttendanceModal from 'src/components/ManageTeamAttendanceModal'
import ManageRefreshmentsModal from 'src/components/ManageRefreshmentsModal'
import LastGameCard from 'src/components/LastGameCard'
import useHomeGames from '@sportninja/common/hooks/useHomeGames'
import ContinueTutorialBanner from 'src/components/ContinueTutorialBanner'
import useHomeDivisionStandings from '@sportninja/common/hooks/useHomeDivisionStandings'
import { processStandingsData } from '@sportninja/common/ts-utils'
import UpgradeBanner from 'src/components/UpgradeBanner'

export const SidebarWrap = styled.div`
  width: 336px;
  min-width: 336px;
  position: relative;
`

export const SidebarInner = styled.div`
  width: inherit;
  position: fixed;
  background: transparent;
  border-radius: 8px;
  max-height: 90vh;
  /* overflow-y: auto; */
  /* hide the scrollbar */
  ::-webkit-scrollbar {
    display: none;
  }
  overflow-x: hidden;
  overflow-y: auto;
  padding-left: 8px;
  padding-right: 8px;
`

interface UserSidebarProps {
  shouldShowOnboardingBanner?: boolean
  onContinueOnboarding?: () => void
  setSidebarEmpty: (value: boolean) => void
  shouldDisplayUpgradeBanner?: boolean
}

const UserSidebar: React.FC<UserSidebarProps> = ({
  shouldShowOnboardingBanner,
  onContinueOnboarding,
  setSidebarEmpty,
  shouldDisplayUpgradeBanner,
}) => {
  const { attendanceState, readAttendanceInformation } = useAttendance()
  const { standingsData, standingsLoading, readStandings } =
    useHomeDivisionStandings()
  const { homeGames, loading, error } = useHomeGames()
  const [isManageAttendanceModalOpen, setIsManageAttendanceModalOpen] =
    useState(false)
  const [isManageRefreshmentsModalOpen, setIsManageRefreshmentsModalOpen] =
    useState(false)
  const [selectedManageRefreshments, setSelectedManageRefreshments] = useState<{
    gameId: string
    rosterId: string
    teamId: string
    teamName: string
    showWaiver: boolean
  } | null>(null)

  const [selectedManageAttendance, setSelectedManageAttendance] = useState<{
    gameId: string
    rosterId: string
    teamId: string
    teamName: string
    showWaiver: boolean
  } | null>(null)

  const onPressManageRefreshments = (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => {
    setSelectedManageRefreshments({
      gameId,
      rosterId,
      teamId,
      teamName,
      showWaiver,
    })
    setIsManageRefreshmentsModalOpen(true)
  }

  const onPressManageAttendance = (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => {
    setSelectedManageAttendance({
      gameId,
      rosterId,
      teamId,
      teamName,
      showWaiver,
    })
    setIsManageAttendanceModalOpen(true)
  }

  useEffect(() => {
    readStandings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasBanner = shouldShowOnboardingBanner && onContinueOnboarding
  const teams = processStandingsData(standingsData)
  const hasStandings =
    !standingsLoading && standingsData && teams && teams.length > 0

  const hasLastGame =
    !loading &&
    !error &&
    homeGames &&
    homeGames?.previousGames &&
    homeGames?.previousGames?.length > 0

  const hasAttendance =
    attendanceState.attendance && attendanceState.attendance.length > 0

  useEffect(() => {
    setSidebarEmpty(
      !hasBanner &&
        !hasStandings &&
        !hasLastGame &&
        !hasAttendance &&
        !shouldDisplayUpgradeBanner &&
        !shouldShowOnboardingBanner
    )
  }, [
    hasBanner,
    hasStandings,
    hasLastGame,
    hasAttendance,
    shouldShowOnboardingBanner,
    setSidebarEmpty,
    shouldDisplayUpgradeBanner,
  ])

  if (
    !hasBanner &&
    !hasStandings &&
    !hasLastGame &&
    !hasAttendance &&
    !shouldDisplayUpgradeBanner
  ) {
    return null
  }

  return (
    <>
      <ManageTeamAttendanceModal
        isManageAttendanceModalOpen={isManageAttendanceModalOpen}
        setIsManageAttendanceModalOpen={setIsManageAttendanceModalOpen}
        selectedManageAttendance={selectedManageAttendance}
        readAttendanceInformation={readAttendanceInformation}
      />
      <ManageRefreshmentsModal
        isManageRefreshmentsModalOpen={isManageRefreshmentsModalOpen}
        setIsManageRefreshmentsModalOpen={setIsManageRefreshmentsModalOpen}
        selectedManageRefreshments={selectedManageRefreshments}
        readAttendanceInformation={readAttendanceInformation}
      />
      <SidebarWrap>
        <SidebarInner>
          {shouldDisplayUpgradeBanner ? (
            <div
              css={css`
                margin-bottom: 16px;
              `}
            >
              <UpgradeBanner />
            </div>
          ) : null}
          {shouldShowOnboardingBanner && onContinueOnboarding ? (
            <div
              css={css`
                margin-bottom: 16px;
              `}
            >
              <ContinueTutorialBanner onContinue={onContinueOnboarding} />
            </div>
          ) : null}
          <DivisionStandingsCarousel
            standingsData={standingsData}
            standingsLoading={standingsLoading}
          />
          {!loading &&
          !error &&
          homeGames &&
          homeGames?.previousGames &&
          homeGames?.previousGames?.length > 0 ? (
            <LastGameCard
              key={homeGames?.previousGames[0]?.id}
              date={homeGames?.previousGames[0]?.starts_at}
              timezone={homeGames?.previousGames[0]?.location?.timezone}
              game_id={homeGames?.previousGames[0]?.id}
              homeTeam={{
                name: homeGames?.previousGames[0]?.home_team?.name,
                logo: homeGames?.previousGames[0]?.home_team?.image,
                score: homeGames?.previousGames[0]?.home_team_score,
                team_id: homeGames?.previousGames[0]?.home_team?.id,
              }}
              visitorTeam={{
                name: homeGames?.previousGames[0]?.visiting_team?.name,
                logo: homeGames?.previousGames[0]?.visiting_team?.image,
                score: homeGames?.previousGames[0]?.visiting_team_score,
                team_id: homeGames?.previousGames[0]?.visiting_team?.id,
              }}
            />
          ) : null}
          <GameAttendanceCarousel
            attendance={attendanceState.attendance}
            readAttendanceInformation={readAttendanceInformation}
            onPressManageAttendance={onPressManageAttendance}
            onPressManageRefreshments={onPressManageRefreshments}
          />
        </SidebarInner>
      </SidebarWrap>
    </>
  )
}

export default UserSidebar
